/**
 * MesFacturesEncart — espace famille : liste des factures du foyer + paiement.
 *
 * Lecture seule + action « Payer » qui passe par PayFiP. En mode mock (flag OFF),
 * le paiement est simulé immédiatement (demande → retour payé) ; en réel, la
 * demande renverra l'URL PayFiP à ouvrir. Le parent ne voit que SES factures
 * (filtrage serveur par responsable + RLS tenant).
 */

import { useEffect, useState, useCallback } from 'react';
import { Receipt, CreditCard, FileText, ChevronDown } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Badge, Alert } from '@/components/ui';
import type { Facture, FactureStatut } from '@roronoa-collectivites/shared-types';

const euros = (c: number) => (c / 100).toFixed(2);
const STATUT_VARIANT: Record<FactureStatut, 'info' | 'success' | 'warning' | 'urgent'> = {
  emise: 'info', envoyee: 'info', partiellement_payee: 'warning', payee: 'success', impayee: 'urgent', annulee: 'warning',
};
const STATUT_LABEL: Record<FactureStatut, string> = {
  emise: 'À payer', envoyee: 'À payer', partiellement_payee: 'Partiellement payée', payee: 'Payée', impayee: 'Impayée', annulee: 'Annulée',
};

export function MesFacturesEncart({ familleId, title = 'Mes factures', excludeService }: { familleId: string; title?: string; excludeService?: string }) {
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [replie, setReplie] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const all = await api.scolaire.facturation.listFactures({ familleId });
      setFactures(excludeService ? all.filter((f) => f.service !== excludeService) : all);
    }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }, [familleId, excludeService]);

  useEffect(() => { void charger(); }, [charger]);

  async function payer(f: Facture) {
    setBusyId(f.id); setError(null); setOk(null);
    try {
      const demande = await api.scolaire.facturation.payer(f.id);
      if (demande.mock) {
        // Mode mock : on simule un paiement abouti.
        const res = await api.scolaire.facturation.payerRetour(f.id, 'paye');
        if (res.facture.statut === 'payee') setOk(`Facture ${f.numero} payée (simulation).`);
        await charger();
      } else {
        // Mode réel : rediriger vers PayFiP.
        window.location.href = demande.url;
      }
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusyId(null); }
  }

  if (loading) return null;
  if (factures.length === 0) return null;

  async function telechargerPdf(f: Facture) {
    try {
      const blob = await api.scolaire.facturation.facturePdfBlob(f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `facture-${f.numero}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'PDF indisponible'); }
  }

  return (
    <Card className="mb-4">
      <CardBody>
        <div className="mb-3 flex items-center gap-2">
          <button type="button" onClick={() => setReplie((v) => !v)} aria-expanded={!replie}
            className="flex items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md">
            <ChevronDown className={`h-4 w-4 text-ink-muted transition-transform duration-200 ${replie ? '-rotate-90' : ''}`} aria-hidden="true" />
            <Receipt className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
            <h2 className="font-display text-base font-semibold text-ink">{title}</h2>
          </button>
        </div>
        {!replie && (<>
        {error && <Alert variant="error">{error}</Alert>}
        {ok && <Alert variant="success">{ok}</Alert>}
        <ul className="divide-y divide-border/60">
          {factures.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="font-mono text-2xs text-ink-muted">{f.numero}</div>
                <div className="text-sm text-ink">{f.periodeDebut} → {f.periodeFin} · {f.service}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold tabular-nums text-ink">{euros(f.montantTtc)} €</span>
                <Badge variant={STATUT_VARIANT[f.statut]}>{STATUT_LABEL[f.statut]}</Badge>
                <Button variant="secondary" size="sm" onClick={() => void telechargerPdf(f)}>
                  <FileText className="h-4 w-4" /> PDF
                </Button>
                {(f.statut === 'emise' || f.statut === 'envoyee' || f.statut === 'partiellement_payee') && (
                  <Button variant="cantine" size="sm" loading={busyId === f.id} onClick={() => void payer(f)}>
                    <CreditCard className="h-4 w-4" /> Payer
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
        </>)}
      </CardBody>
    </Card>
  );
}
